import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { IconsModule } from '@progress/kendo-angular-icons';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { GridModule } from '@progress/kendo-angular-grid';
import { environment } from '../../environments/environment';

interface PortTestResult {
  port: number;
  status: 'not-started' | 'pending' | 'success' | 'error';
  error?: string;
  progress?: number; // Progress percentage (0-100) for timeout visualization
  expanded?: boolean; // For toggling error details visibility
  publicIp?: string; // Public IP address from successful connection
  details?: string; // Success details message
}

@Component({
  selector: 'af-diagnostics',
  imports: [
    ButtonModule,
    InputsModule,
    IconsModule,
    LoaderModule,
    GridModule
  ],
  templateUrl: './diagnostics.component.html',
  styleUrl: './diagnostics.component.scss'
})
export class DiagnosticsComponent implements OnInit {
  target: string = environment.stage.toLowerCase() == 'prod' ?
    `troubleshoot.prod.int.cp.autoform.cloud` 
    :`troubleshoot.${environment.stage.toLowerCase()}.int.cp.aflicensing.com`;
  isDevEnvironment: boolean = environment.stage.toLowerCase() === 'dev';
  testResults: PortTestResult[] = [
    { port: 443, status: 'not-started' },
    { port: 2375, status: 'not-started' },
    { port: 41629, status: 'not-started' }
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    // Removed automatic test execution on page load
    // Tests will only run when the button is clicked
  }

  async runTests() {


    // Reset results
    this.testResults = this.testResults.map(result => ({ 
      ...result, 
      status: 'pending' as const, 
      error: undefined, 
      details: undefined, 
      publicIp: undefined,
      progress: 0 
    }));

    // Test each port
    this.testResults.forEach(async (result) => {
      try {
        await this.testHttpsPortWithProgress(result);
        result.status = 'success';
        result.progress = 100;
        result.error = undefined; // Clear any previous errors
      } catch (error) {
        result.status = 'error';
        result.error = this.getDetailedErrorMessage(error, result.port);
        result.progress = 100;
      }
    });
  }

  private async testHttpsPortWithProgress(result: PortTestResult): Promise<void> {
    const timeoutMs = 10000; // 10 seconds timeout
    const updateInterval = 100; // Update progress every 100ms
    let elapsed = 0;
    
    try {
      const protocol = this.target.includes('://') ? '' : 'https://';
      const url = result.port === 443 ? `${protocol}${this.target}` : `${protocol}${this.target}:${result.port}`;
      
      // Start progress tracking
      const progressInterval = setInterval(() => {
        elapsed += updateInterval;
        result.progress = Math.min((elapsed / timeoutMs) * 100, 100);
      }, updateInterval);
      
      // Create a timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          clearInterval(progressInterval);
          result.progress = 100;
          reject(new Error(`Connection timeout after ${timeoutMs / 1000} seconds`));
        }, timeoutMs);
      });
      
      // Race between fetch and timeout
      const response = await Promise.race([
        fetch(url, {
          method: 'GET',
          cache: 'no-cache',
        }).catch((fetchError) => {
          // Enhance fetch errors with more context
          if (fetchError.name === 'TypeError') {
            if (fetchError.message.includes('Failed to fetch')) {
              throw new Error(`Network request failed: ${fetchError.message}. This could be due to CORS policy, network connectivity issues, or the server being unreachable.`);
            } else if (fetchError.message.includes('NetworkError')) {
              throw new Error(`Network error: ${fetchError.message}. Check your internet connection and firewall settings.`);
            }
          }
          throw fetchError;
        }),
        timeoutPromise
      ]);
      
      // Clear the progress interval on success
      clearInterval(progressInterval);
      
      // Try to extract public IP from response
      try {
        const responseData = await response.json();
        if (responseData && responseData.your_public_ip) {
          result.publicIp = responseData.your_public_ip;
          result.details = `Successfully connected to target from your public IP ${result.publicIp}`;
        }
      } catch (jsonError) {
        // If response is not JSON or doesn't have the expected field, just continue
        // Connection was still successful
        result.details = 'Successfully connected to target';
      }
      
    } catch (error) {
      // Re-throw the original error to preserve all details for analysis
      throw error;
    }
  }

  private async testHttpsPort(port: number): Promise<void> {
    const timeoutMs = 10000; // 10 seconds timeout
    
    try {
      const protocol = this.target.includes('://') ? '' : 'https://';
      const url = port === 443 ? `${protocol}${this.target}` : `${protocol}${this.target}:${port}`;
      
      // Create a timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Connection timeout after ${timeoutMs / 1000} seconds`));
        }, timeoutMs);
      });
      
      // Race between fetch and timeout
      const response = await Promise.race([
        fetch(url, {
          method: 'GET',
        }),
        timeoutPromise
      ]);
      
    } catch (error) {
      if (error instanceof Error && error.message.includes('timeout')) {
        throw error; // Re-throw timeout error with original message
      }
      throw new Error(`HTTPS connection failed on port ${port}.`);
    }
  }

  private async testTcpPort(port: number): Promise<void> {
    // This method is no longer used since we're using HTTPS for all ports
    return this.testHttpsPort(port);
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'not-started':
        return 'Not Started';
      case 'pending':
        return 'Testing...';
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      default:
        return status;
    }
  }

  private getDetailedErrorMessage(error: any, port: number): string {
    const timestamp = new Date().toISOString();
    const protocol = this.target.includes('://') ? '' : 'https://';
    const url = port === 443 ? `${protocol}${this.target}` : `${protocol}${this.target}:${port}`;
    
    let errorDetails = '';
    let errorType = 'Unknown Error';
    let technicalDetails = '';

    if (error instanceof Error) {
      if (error.message.includes('timeout')) {
        errorType = 'Connection Timeout';
        errorDetails = `The connection to ${url} timed out after 10 seconds. This could indicate:
• Your network connection is slow or unstable
• Your firewall or security software is blocking the connection
• Your VPN configuration is interfering with the connection
• Your ISP is throttling or blocking connections to this service`;
        technicalDetails = `Timeout Error: ${error.message}`;
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorType = 'Network Error';
        errorDetails = `Failed to establish a network connection to ${url}. Possible causes:
• Your local DNS resolver cannot resolve ${this.target}
• Your network connection is down or unstable
• Your browser's CORS policy is blocking the request
• Your browser cannot validate the SSL/TLS certificate
• Your firewall is blocking access to port ${port}`;
        technicalDetails = `Network Error: ${error.message}`;
      } else if (error.message.includes('CORS')) {
        // Check if the user is offline before assuming it's a CORS error
        if (!navigator.onLine) {
          errorType = 'Network Connectivity Error';
          errorDetails = `You appear to be offline. Please check your internet connection:
• Your device is not connected to the internet
• Your Wi-Fi or ethernet connection may be disabled
• Your network adapter might need to be reset
• Try reconnecting to your network and run the test again`;
          technicalDetails = `Offline Error (reported as CORS): ${error.message}`;
        } else {
          errorType = 'CORS Policy Error';
          errorDetails = `Cross-Origin Resource Sharing (CORS) policy blocked the request to ${url}:
• Your browser is enforcing security policies that prevent this connection
• Try disabling browser extensions that might modify CORS behavior
• Try accessing from a different browser or incognito/private window
• Your corporate network might be using a proxy that affects CORS headers`;
          technicalDetails = `CORS Error: ${error.message}`;
        }
      } else if (error.message.includes('SSL') || error.message.includes('TLS') || error.message.includes('certificate')) {
        errorType = 'SSL/TLS Certificate Error';
        errorDetails = `SSL/TLS certificate validation failed for ${url}:
• Your browser doesn't trust the certificate authority
• Your system date/time might be incorrect, causing certificate validation to fail
• Your corporate network might be using SSL inspection (man-in-the-middle)
• Your antivirus software might be intercepting secure connections
• Try updating your browser to the latest version`;
        technicalDetails = `SSL/TLS Error: ${error.message}`;
      } else if (error.message.includes('DNS') || error.message.includes('resolve')) {
        errorType = 'DNS Resolution Error';
        errorDetails = `Failed to resolve hostname ${this.target}:
• Your DNS resolver is not working properly
• Your hosts file might have conflicting entries
• Your VPN might be interfering with DNS resolution
• Your ISP's DNS servers might be experiencing issues
• Try using alternative DNS servers (like 8.8.8.8 or 1.1.1.1)`;
        technicalDetails = `DNS Error: ${error.message}`;
      } else {
        errorType = 'Connection Error';
        errorDetails = `Failed to connect to ${url}. Common causes:
• Your local firewall is blocking outbound connections to port ${port}
• Your network administrator has restricted access to this service
• Your browser security settings are preventing the connection
• Your proxy settings are not configured correctly
• Network address translation (NAT) issues on your local network`;
        technicalDetails = `Error: ${error.message}`;
      }
    } else {
      errorType = 'Unknown Error';
      errorDetails = `An unexpected error occurred while testing ${url}:
• Your browser might be experiencing internal issues
• Try restarting your browser or using a different browser
• Check your browser's developer console (F12) for more detailed error messages
• Try disabling all browser extensions and retry the connection`;
      technicalDetails = `Unknown Error: ${JSON.stringify(error)}`;
    }

    return `[${timestamp}] ${errorType}

${errorDetails}

Technical Details:
${technicalDetails}

Connection Information:
• Target: ${url}
• Port: ${port}
• Protocol: ${protocol || 'https://'}
• Hostname: ${this.target}
• Test Duration: 10 seconds (timeout)
• Browser: ${navigator.userAgent}
• Timestamp: ${timestamp}

Troubleshooting Steps:
1. Check your local internet connection
2. Verify your VPN connection if you're using one
3. Check if your corporate firewall is blocking outbound connections to port ${port}
4. Clear your browser cache and cookies
5. Disable browser extensions that might interfere with network requests
6. Try using a different browser
7. Ensure your device's DNS settings are correctly configured
8. If on a corporate network, contact your IT department to verify network policies
9. Check if your antivirus or security software is blocking the connection
10. If the issue persists, capture network logs using browser developer tools and contact support`;
  }
}
